<script setup lang="ts">
  import { ref, computed, watch, nextTick } from "vue";
  import { TriangleAlert } from "lucide-vue-next";
  import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
  import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
  import { Tooltip } from "@/components/ui/tooltip";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { useQualityChecksStore } from "@stores/qualityChecks";
  import { renderMarkdown } from "@utils/renderMarkdown";
  import { scoreVariant, lastFour } from "./quality";
  import type { QualityCheckDetail, QualityCheckRow } from "@/types";

  const props = defineProps<{ customer: QualityCheckRow | null }>();

  const isOpen = defineModel<boolean>("open", { default: false });

  const store = useQualityChecksStore();

  const detail = ref<QualityCheckDetail | null>(null);
  const activeTab = ref("quality");
  const conversationScrollRef = ref<HTMLElement | null>(null);
  const pendingScrollId = ref<string | null>(null);
  const highlightedId = ref<string | null>(null);

  const scores = computed(() => detail.value?.quality?.scores ?? []);
  const problemScores = computed(() => scores.value.filter((score) => score.score <= 3));
  const transcript = computed(() => detail.value?.transcript ?? []);

  const DOT_FILL: Record<string, string> = {
    success: "bg-status-success",
    warning: "bg-status-warning",
    destructive: "bg-status-error",
  };
  const dotFill = (score: number): string => DOT_FILL[scoreVariant(score)];

  const scrollToSource = (sourceId: string): boolean => {
    const el = document.getElementById(`qc-${sourceId}`);
    if (!conversationScrollRef.value || !el) return false;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    highlightedId.value = sourceId;
    return true;
  };
  const scrollWithRetry = (sourceId: string, retries = 8) => {
    nextTick(() => {
      if (!scrollToSource(sourceId) && retries > 0) {
        setTimeout(() => scrollWithRetry(sourceId, retries - 1), 80);
      }
    });
  };
  const goToSource = (sourceId: string) => {
    pendingScrollId.value = sourceId;
    if (activeTab.value === "conversation") scrollWithRetry(sourceId);
    else activeTab.value = "conversation";
  };

  watch(activeTab, (tab) => {
    if (tab === "conversation" && pendingScrollId.value) scrollWithRetry(pendingScrollId.value);
  });

  watch(
    () => props.customer?.id,
    async (id) => {
      activeTab.value = "quality";
      pendingScrollId.value = null;
      highlightedId.value = null;
      detail.value = null;
      if (id) detail.value = await store.fetchDetail(id);
    },
  );
</script>

<template>
  <Sheet
    :open="isOpen"
    @update:open="isOpen = $event">
    <SheetContent
      side="right"
      class="flex w-[40vw] flex-col overflow-hidden p-0 sm:max-w-[40vw]">
      <template v-if="customer">
        <Tabs
          v-model="activeTab"
          default-value="quality"
          class="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div class="flex min-h-12 shrink-0 items-center px-6 pr-14 pt-4">
            <SheetTitle class="heading-title">
              Interview #{{ lastFour(customer.token) || customer.id }}
            </SheetTitle>
          </div>

          <div class="shrink-0 px-6 pb-4 pt-4">
            <TabsList class="w-full">
              <TabsTrigger
                value="quality"
                class="flex-1">
                Quality
              </TabsTrigger>
              <TabsTrigger
                value="conversation"
                class="flex-1">
                Conversation
              </TabsTrigger>
            </TabsList>
          </div>

          <!-- Quality tab -->
          <TabsContent
            value="quality"
            class="flex-1 overflow-y-auto px-6 pb-6 pt-2">
            <div
              v-if="scores.length"
              class="flex flex-col gap-5">
              <section class="flex flex-col gap-1.5">
                <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Scores</h3>
                <Tooltip
                  v-for="dim in scores"
                  :key="dim.dimension"
                  :content="dim.justification">
                  <div class="flex items-center gap-3">
                    <span class="w-32 shrink-0 text-sm capitalize">{{ dim.dimension }}</span>
                    <div class="flex gap-1">
                      <span
                        v-for="n in 5"
                        :key="n"
                        class="size-2 rounded-full"
                        :class="n <= dim.score ? dotFill(dim.score) : 'bg-muted'" />
                    </div>
                    <span class="ml-1 text-sm tabular-nums text-muted-foreground">{{ dim.score }}</span>
                  </div>
                </Tooltip>
              </section>

              <section
                v-for="dim in problemScores"
                :key="dim.dimension"
                class="flex flex-col gap-2">
                <div class="flex items-center gap-2 text-sm font-medium capitalize">
                  <TriangleAlert
                    class="size-4"
                    :class="dim.score <= 2 ? 'text-status-error' : 'text-status-warning'" />
                  {{ dim.dimension }} · {{ dim.score }}/5
                </div>
                <p class="text-sm text-muted-foreground">{{ dim.justification }}</p>
                <button
                  v-for="citation in dim.citations"
                  :key="citation.source_id + citation.quote"
                  type="button"
                  class="flex w-full cursor-pointer items-start gap-2 rounded-r-md border-l-2 border-border bg-muted/40 px-3.5 py-2.5 text-left transition-colors hover:border-primary hover:bg-muted"
                  title="See source message"
                  @click="goToSource(citation.source_id)">
                  <span class="flex-1 text-13 italic leading-snug">« {{ citation.quote }} »</span>
                </button>
              </section>
            </div>
            <EmptyState
              v-else
              title="This respondent has not been scored." />
          </TabsContent>

          <!-- Conversation tab: the AI chat exchange, or the open questions/answers for quanti -->
          <TabsContent
            value="conversation"
            class="flex min-h-0 flex-1 flex-col">
            <div
              ref="conversationScrollRef"
              class="flex-1 overflow-y-auto px-6 pb-6 pt-4">
              <div
                v-if="transcript.length"
                class="space-y-4">
                <div
                  v-for="(turn, index) in transcript"
                  :id="turn.source_id ? `qc-${turn.source_id}` : undefined"
                  :key="turn.source_id ?? `turn-${index}`"
                  :class="turn.speaker === 'respondent' ? 'flex justify-end' : 'flex justify-start'">
                  <div
                    class="max-w-[85%] rounded-lg p-3 transition"
                    :class="[
                      turn.speaker === 'respondent' ? 'border border-border bg-primary/10' : 'bg-muted',
                      highlightedId && turn.source_id === highlightedId ? 'ring-2 ring-primary' : '',
                    ]">
                    <div
                      class="prose prose-sm max-w-none text-sm"
                      v-html="renderMarkdown(turn.text)" />
                  </div>
                </div>
              </div>
              <EmptyState
                v-else
                title="No conversation available." />
            </div>
          </TabsContent>
        </Tabs>
      </template>
    </SheetContent>
  </Sheet>
</template>
